import { useMemo } from 'react';
import chunk from 'lodash/chunk';
import cn from 'classnames';
import { Grid, Badge, BADGE_TYPES, ELEMENT_SIZE } from 'ht-styleguide';

/* Types */
import { TPlans, TPlansBenefits, TPlansInfo } from 'types/plans.types';

/* Constants */
import { PLAN_BENEFITS_IDS } from '@constants/planBenefits';

/* Styles */
import styles from './benefits.module.scss';

/* Types */
type BenefitsProps = {
  plan: TPlans;
  plansInfo?: TPlansInfo;
};

// Move this to a global file if used multiple times
const getSortedList = (benefits: TPlansBenefits[]) =>
  Object.keys(PLAN_BENEFITS_IDS).reduce((acc, elem) => {
    const match = benefits.find(b => b.id === (PLAN_BENEFITS_IDS as Record<string, string>)[elem]);
    if (match) acc.push(match);
    return acc;
  }, [] as TPlansBenefits[]);

const Benefits = ({ plan, plansInfo }: BenefitsProps) => {
  if (!plansInfo) return null;
  const { benefits } = plansInfo.monthly || {};

  const sortedList = getSortedList(benefits!);

  const chunkedList = useMemo(() => chunk(sortedList, 2), [sortedList]);

  return (
    <Grid.FullWidth classes="paddingTop-medium1">
      {chunkedList.map((chunkedBenefits, index) => (
        <Grid.Row classes={styles.benefitsContainer} key={index}>
          {chunkedBenefits.map((benefit, idx) => {
            const { header, subheader, icon, value } = benefit;
            return (
              <>
                {/* DesktopView */}
                <Grid.Column key={`${header}-d-${idx}`} sm={0} lg={1} classes={styles.imageContainer}>
                  <img alt={header} src={icon} className={styles.image} />
                </Grid.Column>
                <Grid.Column sm={0} lg={5} key={`${header}-d`} classes={styles.benefitsTextContainer}>
                  <h6 className="marginBottom-tiny">
                    {header}
                    {value && (
                      <span className="marginLeft-tiny1">
                        <Badge type={BADGE_TYPES.primary} variant="outlined" size={ELEMENT_SIZE.SMALL}>
                          {value} value
                        </Badge>
                      </span>
                    )}
                  </h6>
                  <p className={cn('p2 n700')}>{subheader}</p>
                </Grid.Column>

                {/* Mobile view */}
                <Grid.Row classes={cn('paddingBottom-medium', styles.mobileBenefitsContainer)}>
                  <Grid.Column lg={1} key={`${header}-m-${idx}`} classes={styles.imageContainer}>
                    <img alt={header} src={icon} className={styles.image} />
                  </Grid.Column>
                  <Grid.Column classes={styles.mobileContent} lg={5} key={`${header}-m`}>
                    <h6 className="marginBottom-tiny">
                      {header}
                      {value && (
                        <span className={cn('marginLeft-tiny1', styles.badge)}>
                          <Badge type={BADGE_TYPES.primary} variant="outlined" size={ELEMENT_SIZE.SMALL}>
                            {value} value
                          </Badge>
                        </span>
                      )}
                    </h6>
                    <p className="p2 n700">{subheader}</p>
                  </Grid.Column>
                </Grid.Row>
              </>
            );
          })}
        </Grid.Row>
      ))}
    </Grid.FullWidth>
  );
};

export default Benefits;
